import type { Answers } from "./types";

/**
 * Local-storage backed draft for the step-based Risikoanalyse form.
 * Keyed per token so multiple tokens on the same browser don't collide.
 *
 * Drafts are auto-loaded on form mount (if found), auto-saved on a debounce
 * while the user types, and explicitly saved when the customer clicks
 * the "Zwischenspeichern" button. They are cleared after a successful
 * final submit.
 */

const DEFAULT_PREFIX = "risikoanalyse-step-draft:";

export type Draft = {
  answers: Answers;
  sectionIdx: number;
  subStepIdx: number;
  savedAt: string; // ISO timestamp
};

const keyFor = (token: string, prefix: string) => prefix + token;

export const loadDraft = (
  token: string | undefined,
  prefix: string = DEFAULT_PREFIX,
): Draft | null => {
  if (!token || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(keyFor(token, prefix));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Draft;
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.answers !== "object" || parsed.answers === null) return null;
    return {
      answers: parsed.answers as Answers,
      sectionIdx:
        typeof parsed.sectionIdx === "number" && parsed.sectionIdx >= 0
          ? parsed.sectionIdx
          : 0,
      subStepIdx:
        typeof parsed.subStepIdx === "number" && parsed.subStepIdx >= 0
          ? parsed.subStepIdx
          : 0,
      savedAt: typeof parsed.savedAt === "string" ? parsed.savedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
};

export const saveDraft = (
  token: string | undefined,
  draft: Omit<Draft, "savedAt">,
  prefix: string = DEFAULT_PREFIX,
) => {
  if (!token || typeof window === "undefined") return;
  try {
    const payload: Draft = { ...draft, savedAt: new Date().toISOString() };
    window.localStorage.setItem(keyFor(token, prefix), JSON.stringify(payload));
  } catch {
    // Storage may be full or restricted; silently ignore.
  }
};

export const clearDraft = (
  token: string | undefined,
  prefix: string = DEFAULT_PREFIX,
) => {
  if (!token || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(keyFor(token, prefix));
  } catch {
    // ignore
  }
};
